// Runs interface specs: resolves the body against state into renderable nodes and executes
// actions. It has no network access of its own; operations go through host.call, which in
// POS is the broker. State is a plain object the host may make reactive.
//
// Isolation: interfaces render in the POS document, not in a sandboxed iframe. Specs are data,
// never code: expressions are a closed JSON language with step, depth and size limits, text
// renders as text, and links pass the https check. The only way out is host.call with a
// declared operation name; the backend broker re-checks approval, levels, input and rate
// limits, and writes need a token from a confirmation the host renders from server facts.
// An iframe would add a postMessage bridge without removing any of those checks.
import { CONTAINER_TYPES } from './document.ts'
import { evalExprSafe, truthy, ExprError, evalExpr, exprUser, formatExprValue, MAX_DERIVE_STEPS, type ExprEnv, type ExprScope, type ExprUser } from './expr.ts'
import { checkOperationInput, OPERATIONS } from './operations.ts'
import {
  coerceState, listFillAction, optionsListState, DYNAMIC_ENUMS, ICONS, INTERFACE_CONTAINERS, MAP_KEY_RE, MAX_COMPONENT_NESTING, PICKER_LIMIT,
  type Action, type MaxInterfaceSpec, type StateType
} from './interface.ts'

/** retryAfter: seconds, when the host knows how long a rate_limited refusal lasts (a Retry-After header) */
export interface OperationError { code: string, message: string, retryAfter?: number }

/** background: a rerun of on.load nobody asked for (refresh, a change saved elsewhere); the backend counts it apart and POS stays quiet when it fails */
export interface CallOptions { background?: boolean }

/** Stops a background run at its first failure, keeping what the tool shows */
class BackgroundStop extends Error {}

/** Stops an event after a rate_limited refusal: one toast, no more calls */
class RateLimitStop extends Error {}

/**
 * One run of an action list: the step budget, and whether it is a rerun of on.load (refresh,
 * background or the tool opening), whose state writes are not the user's changes
 */
interface RunContext { steps: number, background: boolean, load: boolean }

/** Time source, replaceable in tests */
export interface RuntimeClock { now (): number, sleep (ms: number): Promise<void> }

const systemClock: RuntimeClock = {
  now: () => Date.now(),
  sleep: ms => new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Refresh coalescing (JDEV-3307). A <refresh/> asked for while on.load runs merges into that
 * run, rerunning it once afterwards when the user changed something since it started. One asked
 * for within REFRESH_GAP_MS of the last run, with nothing changed since, is dropped: on.load just
 * loaded that state. That breaks the loop where on.load writes a filter, the filter reports the
 * value back and its on:change refreshes again. A user's real change always reloads; after
 * MAX_REFRESH_BURST runs in a row, each started within the gap of the one before, the next one
 * waits out the gap.
 */
export const REFRESH_GAP_MS = 2000
export const MAX_REFRESH_BURST = 3
/** After a rate_limited refusal: no refresh or background run for this long, unless the host gives retryAfter */
export const RATE_LIMIT_BACKOFF_MS = 30_000
const MAX_RETRY_AFTER_SECONDS = 600

function sameValue (a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false
  try {
    return JSON.stringify(a) === JSON.stringify(b)
  } catch {
    return false
  }
}

export interface InterfaceHost {
  /** Calls a declared operation by its spec name. Rejects with an OperationError; code 'cancelled' when the user declines a write. */
  call (name: string, input: Record<string, unknown>, options?: CallOptions): Promise<unknown>
  navigate (to: string, id: unknown): void
  toast (message: string, intent: string): void
  /** Interface-authored confirmation (openConfirm). Writes get a separate, host-rendered confirmation inside call. */
  confirm (request: { title: string, message: string, confirmLabel?: string, intent?: string }): Promise<boolean>
  /** Open the same POS PDF the record screen already opens. */
  print? (kind: string, id: unknown): void
  /** Put text on the clipboard; resolves false when the browser refuses */
  copy? (text: string): Promise<boolean>
}

/** Interface state that is not spec state: the open panel */
export interface InterfaceUi { panel: string | null }

export interface InterfaceRuntimeOptions {
  /** Locale and time zone for fmt and dates; the locale defaults to the spec's */
  env?: ExprEnv
  /** Pass a reactive object so the host re-renders when a panel opens */
  ui?: InterfaceUi
  /**
   * The signed-in user, read as user.id and user.name; a getter keeps it current (and reactive)
   * when the host's user loads later. Display data only: it never grants or limits access.
   */
  user?: ExprUser | null | (() => ExprUser | null | undefined)
  /** Tests replace the clock that times refresh coalescing and the rate-limit back-off */
  clock?: RuntimeClock
}

export const MAX_ACTIONS_PER_EVENT = 200
const MAX_TABLE_ROWS = 500
const MAX_TEMPLATE_ITEMS = 500

export interface ResolvedRow { key: string, cells: Record<string, unknown>, row: unknown, actions: number[] }
export interface SearchListResult { count: number, more: boolean }
export interface ResolvedPanel { name: string, title: string, size: string, body: Record<string, unknown>[] }

export class InterfaceRuntime {
  readonly spec: MaxInterfaceSpec
  readonly state: Record<string, unknown>
  readonly ui: InterfaceUi
  readonly env: ExprEnv
  private host: InterfaceHost
  private userSource: InterfaceRuntimeOptions['user']
  private clock: RuntimeClock
  /** Bumped on every state write so actions see derives of the state they changed */
  private version = 0
  /** Bumped when the user's actions change state or save something, never by on.load itself */
  private changes = 0
  /** The rerun of on.load in progress (refresh, background or the tool opening) */
  private loading: Promise<boolean> | null = null
  /** Someone asked for a refresh, with something changed, while loading ran */
  private loadPending = false
  /** changes when the last on.load run started */
  private loadedChanges = 0
  private loadEnded = Number.NEGATIVE_INFINITY
  /** Runs in a row that each started within REFRESH_GAP_MS of the previous one ending */
  private burst = 0
  private backoffUntil = 0
  private rateLimitMessage = ''
  private derivedCache: { version: number, user: string, values: Record<string, unknown> } | null = null
  /** Set while resolve() runs: one derive pass serves the whole render */
  private renderDerived: Record<string, unknown> | null = null

  constructor (spec: MaxInterfaceSpec, state: Record<string, unknown>, host: InterfaceHost, options: InterfaceRuntimeOptions = {}) {
    this.spec = spec
    this.state = state
    this.host = host
    this.ui = options.ui ?? { panel: null }
    this.env = { locale: spec.locale, ...options.env }
    this.userSource = options.user
    this.clock = options.clock ?? systemClock
  }

  /** The user scope: id and name only, null without a signed-in user */
  user (): ExprUser | null {
    return exprUser(typeof this.userSource === 'function' ? this.userSource() : this.userSource)
  }

  private computeDerived (): Record<string, unknown> {
    const values: Record<string, unknown> = {}
    const user = this.user()
    // One step budget for the whole pass, as derives rerun on every render
    const budget = { left: MAX_DERIVE_STEPS }
    for (const [name, e] of Object.entries(this.spec.derive ?? {})) {
      values[name] = evalExprSafe(e, { state: this.state, derived: values, user }, null, this.env, budget)
    }
    return values
  }

  private derived (): Record<string, unknown> {
    if (this.renderDerived) return this.renderDerived
    if (!this.spec.derive) return {}
    // Derives may read user, which a host can fill in after the tool opened
    const user = JSON.stringify(this.user())
    if (!this.derivedCache || this.derivedCache.version !== this.version || this.derivedCache.user !== user) {
      this.derivedCache = { version: this.version, user, values: this.computeDerived() }
    }
    return this.derivedCache.values
  }

  private scope (extra: ExprScope = {}): ExprScope {
    return { ...extra, state: this.state, derived: this.derived(), user: this.user() }
  }

  private eval (expr: unknown, extra: ExprScope = {}, fallback: unknown = null): unknown {
    return evalExprSafe(expr, this.scope(extra), fallback, this.env)
  }

  private text (value: unknown, extra: ExprScope = {}, max = 6000): string {
    if (value && typeof value === 'object' && 'expr' in value) {
      const v = this.eval((value as { expr: unknown }).expr, extra, '')
      return v === null || v === undefined || typeof v === 'object' ? '' : String(v).slice(0, max)
    }
    return typeof value === 'string' ? value : ''
  }

  stateType (name: string): StateType | null {
    return this.spec.state?.[name]?.type ?? null
  }

  /** A write from the host (a field, filter or selection) is the user's */
  setState (name: string, value: unknown): void {
    this.writeState(name, value, true)
  }

  private writeState (name: string, value: unknown, byUser: boolean): void {
    const type = this.stateType(name)
    if (!type) return
    const next = coerceState(type, value)
    if (byUser && !sameValue(this.state[name], next)) this.changes++
    this.state[name] = next
    this.version++
  }

  setIn (name: string, key: unknown, value: unknown): void {
    this.writeIn(name, key, value, true)
  }

  private writeIn (name: string, key: unknown, value: unknown, byUser: boolean): void {
    if (this.stateType(name) !== 'map') return
    const k = typeof key === 'number' || typeof key === 'string' ? String(key) : ''
    if (!MAP_KEY_RE.test(k)) return
    const prev = this.state[name] as Record<string, unknown>
    const next = { ...prev }
    if (value === null || value === undefined || value === '') delete next[k]
    else if (typeof value !== 'object') next[k] = value
    // A list of scalars, e.g. the ticked items of one checklist
    else if (Array.isArray(value) && value.every(v => v === null || typeof v !== 'object')) next[k] = value.slice(0, 200)
    if (byUser && !sameValue(prev?.[k], next[k])) this.changes++
    this.state[name] = next
    this.version++
  }

  // -------------------------------------------------------------------------
  // Actions

  /**
   * Runs one event's actions in order. Errors inside are reported through the host. A
   * rate_limited refusal ends the event quietly after its one toast.
   */
  async run (actions: unknown, extra: ExprScope = {}): Promise<void> {
    try {
      await this.runList(actions, extra, { steps: 0, background: false, load: false })
    } catch (err) {
      if (!(err instanceof RateLimitStop)) throw err
    }
  }

  /**
   * Runs on.load: the tool opening and the refresh button. It runs even right after another
   * load; asked for while one runs, it reruns once after it. While the tool is backing off after a
   * rate_limited refusal it does not run and shows that refusal again.
   */
  async load (): Promise<void> {
    try {
      await this.requestLoad(true)
    } catch (err) {
      if (!(err instanceof RateLimitStop)) throw err
    }
  }

  /**
   * Reruns on.load in the background: only its reads (sent with background: true) and the
   * state they set. Toasts, dialogs, navigation and writes are skipped, and the first failure
   * stops the run quietly so the tool keeps showing what it had. Resolves false when stopped,
   * and when skipped: another load runs or just ended, or the tool is backing off.
   */
  async loadInBackground (): Promise<boolean> {
    if (this.loading || this.backedOffFor() > 0 || this.clock.now() - this.loadEnded < REFRESH_GAP_MS) return false
    try {
      return await this.startLoad(true)
    } catch {
      return false
    }
  }

  /** Milliseconds left of the back-off after a rate_limited refusal; 0 when none */
  backedOffFor (): number {
    return Math.max(0, this.backoffUntil - this.clock.now())
  }

  private backOff (error: OperationError): void {
    const seconds = typeof error.retryAfter === 'number' && Number.isFinite(error.retryAfter) && error.retryAfter > 0
      ? Math.min(error.retryAfter, MAX_RETRY_AFTER_SECONDS)
      : 0
    this.backoffUntil = Math.max(this.backoffUntil, this.clock.now() + (seconds ? seconds * 1000 : RATE_LIMIT_BACKOFF_MS))
    this.rateLimitMessage = error.message
  }

  /**
   * A refresh (manual: the tool opening or the refresh button; otherwise <refresh/>). See
   * REFRESH_GAP_MS. Waiting on a run someone else started, only its rate-limit stop is passed on.
   */
  private async requestLoad (manual: boolean): Promise<void> {
    if (this.backedOffFor() > 0) {
      if (manual && this.rateLimitMessage) this.host.toast(this.rateLimitMessage, 'negative')
      return
    }
    const changed = manual || this.changes !== this.loadedChanges
    if (this.loading) {
      if (changed) this.loadPending = true
      try {
        await this.loading
      } catch (err) {
        if (err instanceof RateLimitStop) throw err
      }
      return
    }
    if (!changed && this.clock.now() - this.loadEnded < REFRESH_GAP_MS) return
    await this.startLoad(false)
  }

  /** Runs on.load, then once more for each batch of refreshes asked for while it ran */
  private startLoad (background: boolean): Promise<boolean> {
    const done = (async () => {
      let quiet = background
      let ok = true
      try {
        do {
          const since = this.clock.now() - this.loadEnded
          this.burst = since < REFRESH_GAP_MS ? this.burst + 1 : 0
          if (!quiet && this.burst >= MAX_REFRESH_BURST) await this.clock.sleep(REFRESH_GAP_MS - Math.max(0, since))
          this.loadPending = false
          if (this.backedOffFor() > 0) return false
          this.loadedChanges = this.changes
          try {
            await this.runList(this.spec.on?.load ?? [], {}, { steps: 0, background: quiet, load: true })
          } catch (err) {
            if (!(err instanceof BackgroundStop)) throw err
            ok = false
          } finally {
            this.loadEnded = this.clock.now()
          }
          // A refresh asked for during a background run is the user's: it runs in full
          quiet = false
        } while (this.loadPending)
        return ok
      } finally {
        this.loading = null
        this.loadPending = false
      }
    })()
    this.loading = done
    return done
  }

  private async runList (actions: unknown, extra: ExprScope, ctx: RunContext): Promise<void> {
    if (!Array.isArray(actions)) return
    for (const action of actions as Action[]) {
      if (++ctx.steps > MAX_ACTIONS_PER_EVENT) throw new ExprError('too many actions for one event')
      await this.runAction(action, extra, ctx)
    }
  }

  private async runAction (a: Action, extra: ExprScope, ctx: RunContext): Promise<void> {
    if (a.when !== undefined && !truthy(this.eval(a.when, extra, false))) return
    // Nobody asked for a background run: it only reads and sets state, never toasts, asks or navigates
    if (ctx.background && a.do !== 'setState' && a.do !== 'setIn' && a.do !== 'callOperation') return
    switch (a.do) {
      case 'setState':
        this.writeState(String(a.state), this.eval(a.value, extra), !ctx.load)
        return
      case 'setIn':
        this.writeIn(String(a.state), this.eval(a.key, extra), this.eval(a.value, extra), !ctx.load)
        return
      case 'callOperation':
        await this.callOperation(a, extra, ctx)
        return
      case 'openConfirm': {
        const ok = await this.host.confirm({
          title: String(a.title ?? ''),
          message: this.text(a.message, extra, 600),
          confirmLabel: typeof a.confirmLabel === 'string' ? a.confirmLabel : undefined,
          intent: typeof a.intent === 'string' ? a.intent : undefined
        })
        if (ok) await this.runList(a.then, extra, ctx)
        return
      }
      case 'navigate':
        this.host.navigate(String(a.to), this.eval(a.id, extra))
        return
      case 'toast':
        this.host.toast(this.text(a.message, extra, 300), typeof a.intent === 'string' ? a.intent : 'info')
        return
      case 'refresh':
        // Inside on.load (lint refuses it there) it would only wait for itself
        if (!ctx.load) await this.requestLoad(false)
        return
      case 'print':
        this.host.print?.(String(a.kind), this.eval(a.id, extra))
        return
      case 'copy': {
        const v = this.eval(a.value, extra, '')
        const text = v === null || v === undefined || typeof v === 'object' ? '' : String(v).slice(0, 5000)
        if (!text || !this.host.copy) return
        const ok = await this.host.copy(text)
        if (ok && typeof a.toast === 'string' && a.toast) this.host.toast(a.toast, 'positive')
        return
      }
      case 'openPanel':
        if (this.spec.panels?.[String(a.panel)]) this.ui.panel = String(a.panel)
        return
      case 'closePanel':
        this.ui.panel = null
    }
  }

  private async callOperation (a: Action, extra: ExprScope, ctx: RunContext): Promise<void> {
    const background = ctx.background
    const name = String(a.operation)
    const ref = this.spec.operations?.[name]
    // A background run reads only; export.xlsx would open a download
    if (background && (!ref || OPERATIONS[ref.operation]?.mode !== 'read' || ref.operation.startsWith('export.'))) return
    const fail = async (error: OperationError) => {
      // Rate limited: the tool backs off, and this event makes no more calls (catch does not run)
      if (error.code === 'rate_limited') {
        this.backOff(error)
        if (background) throw new BackgroundStop(error.code)
        this.host.toast(error.message, 'negative')
        throw new RateLimitStop(error.code)
      }
      if (background) throw new BackgroundStop(error.code)
      if (Array.isArray(a.catch)) await this.runList(a.catch, { ...extra, error }, ctx)
      else if (error.code !== 'cancelled') this.host.toast(error.message, 'negative')
    }
    if (!ref) return fail({ code: 'unknown_operation', message: `Unknown operation ${name}` })

    const input: Record<string, unknown> = {}
    for (const [key, e] of Object.entries((a.input as Record<string, unknown> | undefined) ?? {})) {
      let v: unknown
      try {
        v = evalExpr(e, this.scope(extra), this.env)
      } catch (err) {
        return fail({ code: 'invalid_input', message: `${key}: ${(err as Error).message}` })
      }
      if (v === null || v === undefined) continue
      const field = OPERATIONS[ref.operation]?.input?.[key]
      if (typeof v === 'string' && v === '' && field && !field.required) continue
      input[key] = v
    }
    const problems = checkOperationInput(ref.operation, input)
    if (problems.length) return fail({ code: 'invalid_input', message: problems.map(p => `${p.path}: ${p.message}`).join('; ') })

    let result: unknown
    try {
      result = await (background ? this.host.call(name, input, { background: true }) : this.host.call(name, input))
    } catch (err) {
      return fail(this.operationError(err))
    }
    // A save changed what on.load reads, so a refresh after it always reloads
    if (OPERATIONS[ref.operation]?.mode !== 'read') this.changes++
    if (typeof a.into === 'string') {
      const type = this.stateType(a.into)
      const rows = (result as { rows?: unknown } | null)?.rows
      const byUser = !ctx.load
      if (type === 'list' && Array.isArray(rows)) this.writeState(a.into, rows, byUser)
      else if (type === 'record' && Array.isArray(rows)) this.writeState(a.into, rows[0] ?? null, byUser)
      else this.writeState(a.into, result, byUser)
    }
    await this.runList(a.then, { ...extra, result }, ctx)
  }

  private operationError (err: unknown): OperationError {
    const e = err as Partial<OperationError> | undefined
    return {
      code: typeof e?.code === 'string' ? e.code : 'failed',
      message: typeof e?.message === 'string' ? e.message : 'Failed',
      ...(typeof e?.retryAfter === 'number' ? { retryAfter: e.retryAfter } : {})
    }
  }

  // -------------------------------------------------------------------------
  // Rendering
  //
  // Resolved nodes carry the expression scope they were resolved in as `scope` when it is
  // not the root one (inside a repeat, list or component), so the host runs their handlers
  // with the same item, loop and props the node was drawn with.

  /** The body as document nodes plus control nodes (field, button, datatable) for the host renderer. */
  resolve (): Record<string, unknown>[] {
    return this.withRender(() => this.resolveList(this.spec.body))
  }

  /** The open panel, resolved like the body */
  resolvePanel (): ResolvedPanel | null {
    const name = this.ui.panel
    const def = name ? this.spec.panels?.[name] : null
    if (!name || !def) return null
    return this.withRender(() => ({
      name,
      title: this.text(def.title, {}, 120),
      size: typeof def.size === 'string' ? def.size : 'md',
      body: this.resolveList(def.body)
    }))
  }

  private withRender<T> (fn: () => T): T {
    const outer = this.renderDerived
    if (!outer) this.renderDerived = this.spec.derive ? this.computeDerived() : {}
    try {
      return fn()
    } finally {
      if (!outer) this.renderDerived = null
    }
  }

  private resolveList (nodes: unknown, extra: ExprScope = {}, slot: Record<string, unknown>[] | null = null, nesting = 0): Record<string, unknown>[] {
    if (!Array.isArray(nodes)) return []
    const out: Record<string, unknown>[] = []
    for (const n of nodes) {
      const node = n as Record<string, unknown>
      if (!node || typeof node !== 'object') continue
      if (node.type === 'slot') {
        if (slot) out.push(...slot)
        continue
      }
      if (node.type === 'repeat') {
        if (node.visible !== undefined && !truthy(this.eval(node.visible, extra, false))) continue
        out.push(...this.resolveRepeat(node, extra, slot, nesting))
        continue
      }
      if (node.type === 'use') {
        if (node.visible !== undefined && !truthy(this.eval(node.visible, extra, false))) continue
        out.push(...this.resolveUse(node, extra, slot, nesting))
        continue
      }
      const r = this.resolveNode(node, extra, slot, nesting)
      if (r) out.push(r)
    }
    return out
  }

  private listOfObjects (value: unknown, limit = MAX_TABLE_ROWS): Record<string, unknown>[] {
    return (Array.isArray(value) ? value : []).slice(0, limit).filter(row => !!row && typeof row === 'object' && !Array.isArray(row)) as Record<string, unknown>[]
  }

  private resolveComputedRows (value: unknown, extra: ExprScope = {}): Record<string, unknown>[] {
    if (Array.isArray(value)) return this.listOfObjects(value)
    if (value && typeof value === 'object' && 'expr' in value) {
      return this.listOfObjects(this.eval((value as { expr: unknown }).expr, extra, []))
    }
    return []
  }

  /** Scope for one element of a repeat, list or timeline */
  private itemScope (node: Record<string, unknown>, extra: ExprScope, item: unknown, index: number): ExprScope {
    const loop = typeof node.as === 'string'
      ? { ...((extra.loop as Record<string, unknown> | undefined) ?? {}), [node.as]: item }
      : extra.loop
    return { ...extra, item, index, ...(loop ? { loop } : {}) }
  }

  private templateItems (node: Record<string, unknown>, extra: ExprScope): unknown[] {
    const list = this.eval(node.items, extra, [])
    const limit = Math.min(typeof node.maxItems === 'number' ? node.maxItems : MAX_TEMPLATE_ITEMS, MAX_TEMPLATE_ITEMS)
    return (Array.isArray(list) ? list : []).slice(0, limit)
  }

  /** A repeat renders its children once per item, in place: inside a grid each child is a cell */
  private resolveRepeat (node: Record<string, unknown>, extra: ExprScope, slot: Record<string, unknown>[] | null, nesting: number): Record<string, unknown>[] {
    const items = this.templateItems(node, extra)
    if (!items.length) {
      return typeof node.empty === 'string' && node.empty ? [{ type: 'text', text: node.empty, tone: 'muted' }] : []
    }
    const out: Record<string, unknown>[] = []
    items.forEach((item, i) => out.push(...this.resolveList(node.children, this.itemScope(node, extra, item, i), slot, nesting)))
    return out
  }

  private resolveUse (node: Record<string, unknown>, extra: ExprScope, slot: Record<string, unknown>[] | null, nesting: number): Record<string, unknown>[] {
    const def = this.spec.components?.[String(node.component)]
    if (!def || nesting >= MAX_COMPONENT_NESTING) return []
    const props: Record<string, unknown> = {}
    for (const param of def.params ?? []) {
      const e = (node.with as Record<string, unknown> | undefined)?.[param]
      props[param] = e === undefined ? null : this.eval(e, extra)
    }
    // The use node's children fill the component's slot, drawn in the caller's scope
    const filled = Array.isArray(node.children) ? this.resolveList(node.children, extra, slot, nesting) : []
    return this.resolveList(def.body, { props }, filled, nesting + 1)
  }

  private resolveNode (node: Record<string, unknown>, extra: ExprScope, slot: Record<string, unknown>[] | null, nesting: number): Record<string, unknown> | null {
    if (node.visible !== undefined && !truthy(this.eval(node.visible, extra, false))) return null
    const { visible: _v, ...rest } = node
    const out: Record<string, unknown> = { ...rest }
    if (Object.keys(extra).length) out.scope = extra
    this.resolveEnums(out, extra)
    if (node.type === 'icon') this.resolveEnum(out, 'name', this.iconNames(), extra)
    switch (node.type) {
      case 'heading':
        out.text = this.text(node.text, extra, 200)
        if (node.eyebrow !== undefined) out.eyebrow = this.text(node.eyebrow, extra, 60)
        break
      case 'text':
        if (node.text !== undefined) out.text = this.text(node.text, extra)
        if (Array.isArray(node.spans)) {
          out.spans = (node.spans as Record<string, unknown>[]).map(span => {
            const r = { ...span, text: this.text(span.text, extra, 1000) }
            this.resolveEnums(r, extra)
            return r
          })
        }
        break
      case 'callout': out.text = this.text(node.text, extra, 1500); break
      case 'metric': {
        if (node.value && typeof node.value === 'object') {
          const v = this.eval((node.value as { expr: unknown }).expr, extra)
          out.value = typeof v === 'number' || typeof v === 'string' ? v : null
        }
        if (node.caption !== undefined) out.caption = this.text(node.caption, extra, 160)
        break
      }
      case 'badge': out.text = this.text(node.text, extra, 80); break
      case 'avatar': out.name = this.text(node.name, extra, 120); break
      case 'empty':
        out.title = this.text(node.title, extra, 160)
        if (node.text !== undefined) out.text = this.text(node.text, extra, 600)
        break
      case 'disclosure':
        out.title = this.text(node.title, extra, 160)
        if (node.summary !== undefined) out.summary = this.text(node.summary, extra, 160)
        break
      case 'card':
        if (node.title !== undefined) out.title = this.text(node.title, extra, 160)
        if (node.eyebrow !== undefined) out.eyebrow = this.text(node.eyebrow, extra, 60)
        out.selected = node.selected !== undefined && truthy(this.eval(node.selected, extra, false))
        break
      case 'progress': {
        const value = Number(this.eval(node.value, extra, 0))
        const max = node.max === undefined ? 100 : Number(this.eval(node.max, extra, 100))
        out.value = Number.isFinite(value) ? value : 0
        out.max = Number.isFinite(max) && max > 0 ? max : 100
        if (node.label !== undefined) out.label = this.text(node.label, extra, 80)
        break
      }
      case 'keyvalue':
        out.items = ((node.items as Record<string, unknown>[] | undefined) ?? []).flatMap(item => {
          if (item.visible !== undefined && !truthy(this.eval(item.visible, extra, false))) return []
          const v = this.eval(item.value, extra, null)
          const value = item.format !== undefined
            ? formatExprValue(v, item.format, item.currency, this.env)
            : v === null || v === undefined || typeof v === 'object' ? '' : String(v)
          const r: Record<string, unknown> = { label: this.text(item.label, extra, 80), value, mono: item.mono === true, intent: item.intent }
          this.resolveEnums(r, extra)
          return [r]
        })
        break
      case 'image': {
        const id = this.eval(node.id, extra, null)
        out.id = typeof id === 'number' || typeof id === 'string' ? id : null
        if (node.alt !== undefined) out.alt = this.text(node.alt, extra, 160)
        break
      }
      case 'list': return this.resolveListNode(out, extra, slot, nesting)
      case 'timeline': return this.resolveTimeline(out, extra, slot, nesting)
      case 'field': return this.resolveField(out, extra)
      case 'datenav':
        out.value = typeof node.state === 'string' ? this.state[node.state] : ''
        return out
      case 'menu':
        out.items = ((node.items as Record<string, unknown>[] | undefined) ?? []).flatMap((item, index) => {
          if (item.visible !== undefined && !truthy(this.eval(item.visible, extra, false))) return []
          const r: Record<string, unknown> = {
            ...item,
            index,
            label: this.text(item.label, extra, 60),
            disabled: item.disabled !== undefined && truthy(this.eval(item.disabled, extra, true))
          }
          this.resolveEnums(r, extra)
          return [r]
        })
        return out
      case 'filterbar':
        out.filters = ((node.filters as Record<string, unknown>[] | undefined) ?? []).map(f => this.resolveFilter({ ...f, type: 'field' }, extra))
        return out
      case 'button':
        out.label = this.text(node.label, extra, 60)
        out.disabled = node.disabled !== undefined && truthy(this.eval(node.disabled, extra, true))
        break
      case 'datatable': return this.resolveTable(out, extra)
      case 'calendar': return this.resolveCalendar(out, extra)
      case 'board': return this.resolveBoard(out, extra)
      case 'detail': return this.resolveDetail(out, extra)
      case 'table':
        out.rows = this.resolveComputedRows(node.rows, extra)
        break
      case 'chart': {
        const chart = { ...((node.chart as Record<string, unknown> | undefined) ?? {}) }
        if (chart.data && typeof chart.data === 'object' && 'expr' in chart.data) {
          chart.data = this.listOfObjects(this.eval((chart.data as { expr: unknown }).expr, extra, []))
        }
        // The bound form ({ data, category, series: [{ field, name }] }) becomes the
        // chart compiler's categories and series; an empty result draws nothing
        if (Array.isArray(chart.data) && typeof chart.category === 'string') {
          const rows = chart.data as Record<string, unknown>[]
          const { data: _d, category, series, ...restChart } = chart
          const num = (v: unknown) => typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v)) ? Number(v) : null
          if (!rows.length) return { type: 'empty', title: typeof node.title === 'string' ? node.title : '', scope: out.scope }
          out.chart = {
            ...restChart,
            categories: rows.map(r => String(r[category as string] ?? '').slice(0, 80)),
            series: ((series as { field: string, name: string }[] | undefined) ?? []).map(s => ({ name: s.name, data: rows.map(r => num(r[s.field])) }))
          }
          break
        }
        out.chart = chart
        break
      }
    }
    const type = String(node.type)
    if ((CONTAINER_TYPES as readonly string[]).includes(type) || (INTERFACE_CONTAINERS as readonly string[]).includes(type)) {
      const children = this.resolveList(node.children, extra, slot, nesting)
      if ((type === 'split' || type === 'sidebar') && children.length !== 2) return { ...out, type: 'stack', ratio: undefined, children }
      if (type === 'tabs' && children.length < 2) return children[0] ?? { ...out, type: 'stack', children }
      out.children = children
    }
    return out
  }

  /** intent, icon and tone given as { expr }: the value when it is one of the allowed ones */
  private resolveEnums (obj: Record<string, unknown>, extra: ExprScope): void {
    for (const [prop, allowed] of Object.entries(DYNAMIC_ENUMS)) this.resolveEnum(obj, prop, prop === 'icon' ? this.iconNames() : allowed, extra)
  }

  /** Built-in icons and the tool's own */
  private iconNames (): readonly string[] {
    return this.spec.icons ? [...ICONS, ...Object.keys(this.spec.icons)] : ICONS
  }

  private resolveEnum (obj: Record<string, unknown>, prop: string, allowed: readonly string[], extra: ExprScope): void {
    const v = obj[prop]
    if (!v || typeof v !== 'object' || !('expr' in v)) return
    const value = this.text(v, extra, 40)
    if (allowed.includes(value)) obj[prop] = value
    else delete obj[prop]
  }

  private resolveListNode (node: Record<string, unknown>, extra: ExprScope, slot: Record<string, unknown>[] | null, nesting: number): Record<string, unknown> {
    const items = this.templateItems(node, extra)
    const template = Array.isArray(node.children) && node.children.length > 0
    return {
      ...node,
      items: items.map((item, index) => {
        const scope = this.itemScope(node, extra, item, index)
        if (!template) {
          return { key: String(index), text: item === null || item === undefined || typeof item === 'object' ? '' : String(item), scope, selected: false }
        }
        const id = item && typeof item === 'object' ? (item as { id?: unknown }).id : undefined
        return {
          key: typeof id === 'string' || typeof id === 'number' ? String(id) : String(index),
          children: this.resolveList(node.children, scope, slot, nesting),
          selected: node.selected !== undefined && truthy(this.eval(node.selected, scope, false)),
          scope
        }
      }),
      template
    }
  }

  private resolveTimeline (node: Record<string, unknown>, extra: ExprScope, slot: Record<string, unknown>[] | null, nesting: number): Record<string, unknown> {
    return {
      ...node,
      items: this.templateItems(node, extra).map((item, index) => {
        const scope = this.itemScope(node, extra, item, index)
        return {
          key: String(index),
          time: node.time !== undefined ? this.text({ expr: node.time }, scope, 60) : '',
          intent: node.intent !== undefined ? this.text({ expr: node.intent }, scope, 20) : '',
          children: this.resolveList(node.children, scope, slot, nesting)
        }
      })
    }
  }

  /** Map each catalog row so picker paging is not clipped by list or expression caps. */
  private evalOptionsList (expr: unknown, extra: ExprScope): unknown {
    if (expr && typeof expr === 'object' && !Array.isArray(expr) && 'map' in expr) {
      const args = (expr as { map: unknown }).map
      if (Array.isArray(args) && args.length >= 2) {
        const source = this.eval(args[0], extra, [])
        return (Array.isArray(source) ? source : []).map(item => this.eval(args[1], { ...extra, item }, null))
      }
    }
    return this.eval(expr, extra, [])
  }

  private resolveOptions (raw: unknown, extra: ExprScope): unknown[] | undefined {
    if (Array.isArray(raw)) return raw
    if (raw && typeof raw === 'object' && 'expr' in raw) {
      const v = this.evalOptionsList((raw as { expr: unknown }).expr, extra)
      type Option = { label: string, value: string | number }
      return (Array.isArray(v) ? v : []).flatMap((o): Array<Option | { label: string, options: Option[] }> => {
        if (!o || typeof o !== 'object') return []
        const group = o as { label?: unknown, options?: unknown, value?: unknown }
        if (Array.isArray(group.options)) {
          const options = group.options.flatMap(child => {
            if (!child || typeof child !== 'object') return []
            const value = (child as { value?: unknown }).value
            const label = (child as { label?: unknown }).label
            return (typeof value === 'string' || typeof value === 'number') ? [{ label: String(label ?? value).slice(0, 80), value }] : []
          })
          return options.length ? [{ label: String(group.label ?? '').slice(0, 80), options }] : []
        }
        const value = group.value
        return (typeof value === 'string' || typeof value === 'number') ? [{ label: String(group.label ?? value).slice(0, 80), value }] : []
      })
    }
    return undefined
  }

  private resolveField (node: Record<string, unknown>, extra: ExprScope = {}): Record<string, unknown> {
    const name = String(node.state)
    let value = this.state[name]
    let stateKey: string | undefined
    if (node.key !== undefined) {
      const k = this.eval(node.key, extra, null)
      stateKey = typeof k === 'string' || typeof k === 'number' ? String(k) : ''
      const map = this.state[name]
      value = map && typeof map === 'object' && !Array.isArray(map) && Object.prototype.hasOwnProperty.call(map, stateKey)
        ? (map as Record<string, unknown>)[stateKey]
        : null
    }
    return {
      ...node,
      value,
      ...(stateKey !== undefined ? { stateKey } : {}),
      disabled: node.disabled !== undefined && truthy(this.eval(node.disabled, extra, true)),
      options: this.resolveOptions(node.options, extra),
      optionsState: optionsListState(node.options)
    }
  }

  private resolveFilter (node: Record<string, unknown>, extra: ExprScope = {}): Record<string, unknown> {
    if (node.input === 'daterange') {
      return {
        ...node,
        value: typeof node.period === 'string' ? this.state[node.period] : '',
        periodValue: typeof node.period === 'string' ? this.state[node.period] : '',
        fromValue: typeof node.from === 'string' ? this.state[node.from] : '',
        toValue: typeof node.to === 'string' ? this.state[node.to] : '',
        disabled: false,
        options: undefined
      }
    }
    return this.resolveField(node, extra)
  }

  private resolveTable (node: Record<string, unknown>, extra: ExprScope = {}): Record<string, unknown> {
    const rowKey = typeof node.rowKey === 'string' ? node.rowKey : 'id'
    const columns = (node.columns as Record<string, unknown>[]) ?? []
    const list = this.eval(node.rows, extra, [])
    const limit = Math.min(typeof node.maxRows === 'number' ? node.maxRows : MAX_TABLE_ROWS, MAX_TABLE_ROWS)
    const rowActions = (node.rowActions as Record<string, unknown>[] | undefined) ?? []
    const rows: ResolvedRow[] = []
    for (const row of (Array.isArray(list) ? list : []).slice(0, limit)) {
      if (!row || typeof row !== 'object') continue
      const r = row as Record<string, unknown>
      const rawKey = Object.prototype.hasOwnProperty.call(r, rowKey) ? r[rowKey] : undefined
      const key = typeof rawKey === 'string' || typeof rawKey === 'number' ? String(rawKey) : ''
      const cells: Record<string, unknown> = {}
      for (const c of columns) {
        const k = String(c.key)
        const v = c.value !== undefined ? this.eval(c.value, { ...extra, row }) : (Object.prototype.hasOwnProperty.call(r, k) ? r[k] : null)
        cells[k] = v === undefined || (v !== null && typeof v === 'object') ? null : v
      }
      const actions = rowActions.flatMap((ra, i) => ra.visible === undefined || truthy(this.eval(ra.visible, { ...extra, row }, false)) ? [i] : [])
      rows.push({ key, cells, row, actions })
    }
    const edits: Record<string, Record<string, unknown>> = {}
    for (const c of columns) {
      const edit = c.edit as { state?: string } | undefined
      if (edit?.state) edits[String(c.key)] = (this.state[edit.state] as Record<string, unknown>) ?? {}
    }
    const select = typeof node.select === 'string' ? node.select : null
    const count = node.count !== undefined ? this.eval(node.count, extra, null) : null
    return {
      ...node,
      rowKey,
      narrow: node.narrow ?? (columns.length > 4 ? 'cards' : 'scroll'),
      resolvedRows: rows,
      total: typeof count === 'number' && Number.isFinite(count) ? count : (Array.isArray(list) ? list.length : 0),
      pageState: typeof node.page === 'string' ? node.page : null,
      page: typeof node.page === 'string' ? (this.state[node.page] ?? 1) : null,
      sortValue: typeof node.sort === 'string' ? this.state[node.sort] : null,
      sortDirValue: typeof node.sortDir === 'string' ? this.state[node.sortDir] : null,
      selected: select ? ((this.state[select] as unknown[]) ?? []).map(String) : null,
      edits
    }
  }

  private resolveEventRows (list: unknown, rowKey = 'id'): ResolvedRow[] {
    const rows: ResolvedRow[] = []
    for (const row of (Array.isArray(list) ? list : []).slice(0, MAX_TABLE_ROWS)) {
      if (!row || typeof row !== 'object') continue
      const r = row as Record<string, unknown>
      const rawKey = Object.prototype.hasOwnProperty.call(r, rowKey) ? r[rowKey] : undefined
      const key = typeof rawKey === 'string' || typeof rawKey === 'number' ? String(rawKey) : ''
      rows.push({ key, cells: r, row, actions: [] })
    }
    return rows
  }

  private resolveCalendar (node: Record<string, unknown>, extra: ExprScope = {}): Record<string, unknown> {
    const list = this.eval(node.events, extra, [])
    return {
      ...node,
      resolvedRows: this.resolveEventRows(list),
      fromValue: node.from !== undefined ? this.eval(node.from, extra, null) : null,
      toValue: node.to !== undefined ? this.eval(node.to, extra, null) : null,
      periodValue: node.period !== undefined ? this.eval(node.period, extra, null) : null
    }
  }

  private resolveBoard (node: Record<string, unknown>, extra: ExprScope = {}): Record<string, unknown> {
    const columns = ((node.columns as Record<string, unknown>[] | undefined) ?? []).map(column => ({
      ...column,
      resolvedRows: this.resolveEventRows(this.eval(column.rows, extra, []))
    }))
    return { ...node, columns }
  }

  private resolveDetail (node: Record<string, unknown>, extra: ExprScope = {}): Record<string, unknown> {
    const row = this.eval(node.row, extra, null)
    return {
      ...node,
      resolvedRow: row && typeof row === 'object' && !Array.isArray(row) ? row : null
    }
  }

  canSearchList (name: string): boolean {
    return listFillAction(this.spec, name) !== null
  }

  /** Reloads a catalog list state with query/offset so a picker can search and page the whole pool. */
  async searchList (name: string, query: string, offset = 0): Promise<SearchListResult> {
    const source = listFillAction(this.spec, name)
    const ref = source ? this.spec.operations?.[source.operation] : null
    if (!source || !ref) return { count: 0, more: false }
    const extra: ExprScope = { event: { query, offset } }
    const inputExpr: Record<string, unknown> = {
      ...source.input,
      query: { var: 'event.query' },
      limit: PICKER_LIMIT,
      offset: { var: 'event.offset' }
    }
    const input: Record<string, unknown> = {}
    for (const [key, e] of Object.entries(inputExpr)) {
      let v: unknown
      try {
        v = evalExpr(e, this.scope(extra), this.env)
      } catch (err) {
        this.host.toast(`${key}: ${(err as Error).message}`, 'negative')
        return { count: 0, more: false }
      }
      if (v === null || v === undefined) continue
      const field = OPERATIONS[ref.operation]?.input?.[key]
      if (typeof v === 'string' && v === '' && field && !field.required) continue
      input[key] = v
    }
    const problems = checkOperationInput(ref.operation, input)
    if (problems.length) {
      this.host.toast(problems.map(p => `${p.path}: ${p.message}`).join('; '), 'negative')
      return { count: 0, more: false }
    }
    let result: unknown
    try {
      result = await this.host.call(source.operation, input)
    } catch (err) {
      const e = this.operationError(err)
      if (e.code === 'rate_limited') this.backOff(e)
      if (e.code !== 'cancelled') this.host.toast(e.message, 'negative')
      return { count: 0, more: false }
    }
    const rows = (result as { rows?: unknown } | null)?.rows
    const next = Array.isArray(rows) ? rows : []
    const count = typeof (result as { count?: unknown } | null)?.count === 'number'
      ? (result as { count: number }).count
      : next.length
    const prev = offset > 0 && Array.isArray(this.state[name]) ? this.state[name] as unknown[] : []
    const seen = new Set<string>()
    const merged: unknown[] = []
    for (const row of [...prev, ...next]) {
      if (!row || typeof row !== 'object') continue
      const id = (row as { id?: unknown }).id
      const key = typeof id === 'string' || typeof id === 'number' ? String(id) : ''
      if (key) {
        if (seen.has(key)) continue
        seen.add(key)
      }
      merged.push(row)
    }
    this.state[name] = merged
    this.version++
    return { count, more: merged.length < count }
  }

  /** Selection change from a datatable: keeps the original key types of the rows */
  setSelection (name: string, keys: string[], rows: ResolvedRow[], rowKey: string): void {
    const byKey = new Map(rows.map(r => [r.key, (r.row as Record<string, unknown>)[rowKey]]))
    const current = ((this.state[name] as unknown[]) ?? []).filter(k => !byKey.has(String(k)))
    this.setState(name, [...current, ...keys.map(k => byKey.get(k) ?? k)])
  }
}
